import React, { useMemo, useState } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import {
  usePaginatedListQuery,
  useUpdatePatchMutation,
  useDeleteMutation,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { ColumnDef } from '@tanstack/react-table';
import ItemUnitCreateOrUpdate from 'components/Form/Catalog/ItemUnitCreateOrUpdate';

interface ItemUnit {
  id: string;
  unitName: string;
  notes: string;
  isActive: boolean;
}

const ItemUnitsList: React.FC = () => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<string | undefined>('unitname');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(undefined);

  const { data, isLoading, isError, error, refetch } = usePaginatedListQuery<ItemUnit>(
    API_ENDPOINTS.SAAS.ITEM_UNITS.BASE,
    {
      pageNumber,
      pageSize,
      search: search || undefined,
      sortBy,
      sortDirection,
    },
  );

  const itemUnits: ItemUnit[] = useMemo(() => {
    return data?.items ?? [];
  }, [data]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (isModalOpen) {
      setSelectedUnitId(undefined);
    }
  };

  const handleAddNew = () => {
    setSelectedUnitId(undefined);
    setIsModalOpen(true);
  };

  const handleEdit = (id: string) => {
    setSelectedUnitId(id);
    setIsModalOpen(true);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const columns: ColumnDef<ItemUnit>[] = useMemo(
    () => [
      {
        header: 'Unit Name',
        accessorKey: 'unitName',
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
      },
      {
        header: 'Status',
        accessorKey: 'isActive',
        cell: ({ row }) => <span>{row.original.isActive ? 'Active' : 'Inactive'}</span>,
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: ({ row }) => {
          const { id, isActive } = row.original;
          const { mutate: toggleStatus } = useUpdatePatchMutation(
            `${API_ENDPOINTS.SAAS.ITEM_UNITS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.ITEM_UNITS.BASE,
          );

          const { mutate: deleteItemUnit } = useDeleteMutation(
            `${API_ENDPOINTS.SAAS.ITEM_UNITS.BASE}/${id}`,
            API_ENDPOINTS.SAAS.ITEM_UNITS.BASE,
          );

          const handleToggleStatus = () => {
            if (
              window.confirm(
                `Are you sure you want to ${isActive ? 'deactivate' : 'activate'} this unit?`,
              )
            ) {
              toggleStatus({ data: { isActive: !isActive }, params: { id } });
            }
          };

          const handleDelete = () => {
            if (window.confirm('Are you sure you want to delete this item unit?')) {
              deleteItemUnit(id);
            }
          };

          return (
            <div className="d-flex gap-2 align-items-center">
              <Button size="sm" color="primary" outline onClick={() => handleEdit(id)}>
                Edit
              </Button>
              <i
                className={`bx ${isActive ? 'bx-check-circle' : 'bx-x-circle'} ${
                  isActive ? 'text-success' : 'text-warning'
                } cursor-pointer`}
                title={isActive ? 'Deactivate' : 'Activate'}
                style={{ fontSize: '1.2rem' }}
                onClick={handleToggleStatus}
              ></i>
              <i
                className="bx bx-trash text-muted cursor-pointer"
                title="Delete"
                style={{ fontSize: '1.2rem' }}
                onClick={handleDelete}
              ></i>
            </div>
          );
        },
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Item Units" breadcrumbItem="List" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">
                    {error?.message || 'Failed to load item units. Please try again later.'}
                  </Alert>
                ) : (
                  <>
                    <div className="mb-3">
                      <Button color="success" onClick={handleAddNew}>
                        Add Item Unit
                      </Button>
                    </div>
                    <TableContainer
                      columns={columns}
                      data={itemUnits}
                      isGlobalFilter={true}
                      isAddButton={false}
                      isPagination={true}
                      isCustomPageSize={true}
                      buttonClass="btn btn-success btn-rounded"
                      buttonName="Add Item Unit"
                      tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      paginationClass="pagination"
                      SearchPlaceholder="Search item units..."
                    />

                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <small className="text-muted">
                        Showing {itemUnits.length > 0 ? (pageNumber - 1) * pageSize + 1 : 0} to{' '}
                        {Math.min(pageNumber * pageSize, data?.totalCount || 0)} of{' '}
                        {data?.totalCount || 0} items
                      </small>

                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                          disabled={!data?.hasPreviousPage || isLoading}
                        >
                          Previous
                        </Button>
                        <span className="align-self-center small">
                          Page {data?.pageNumber || 1} of {data?.totalPages || 1}
                        </span>
                        <Button
                          size="sm"
                          outline
                          color="primary"
                          onClick={() => setPageNumber((p) => p + 1)}
                          disabled={!data?.hasNextPage || isLoading}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ItemUnitCreateOrUpdate
        isOpen={isModalOpen}
        toggle={toggleModal}
        unitId={selectedUnitId}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default ItemUnitsList;
